import { useField } from 'formik';
import { FC, useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import FormField from 'shared/components/form/FormField';
import { tw } from 'utils/tailwindUtils';
import './stylesPhone.css';

interface InternationalPhonesProps {
  label?: string;
  name: string;
  defaultCountry?: string;
  disabled?: boolean;
  /**
   * `default`: label arriba y borde/fondo iguales a los demás inputs del
   * rediseño (`FormField`). `floating`: label flotante estilo Material, solo
   * para formularios legacy (MUI outlined) que todavía no migraron.
   */
  variant?: 'default' | 'floating';
}

const InternationalPhones: FC<InternationalPhonesProps> = ({
  label,
  defaultCountry = 'AR',
  disabled = false,
  variant = 'default',
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  useEffect(() => {
    setHasValue(!!field.value);
  }, [field.value]);

  const handlePhoneChange = (value: string | undefined) => {
    helpers.setValue(value);
    setHasValue(!!value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    helpers.setTouched(true);
  };

  const hasError = meta.touched && meta.error;

  const phoneInput = (
    <PhoneInput
      international
      defaultCountry={defaultCountry as any}
      value={field.value}
      onChange={handlePhoneChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      className="phone-input-element"
      error={false}
      smartCaret={false}
      disabled={disabled}
    />
  );

  if (variant === 'floating') {
    return (
      <div className="phone-input-container">
        <div
          className={`phone-input-wrapper ${isFocused ? 'focused' : ''} ${
            hasError ? 'error' : ''
          } ${hasValue ? 'has-value' : ''}`}
        >
          {label && (
            <label
              className={`phone-input-label ${
                hasError
                  ? 'floating-error'
                  : isFocused
                  ? 'floating'
                  : 'floating-done'
              }`}
            >
              {(meta.touched || hasError || hasValue || isFocused) && label}
            </label>
          )}
          {phoneInput}
        </div>
        {hasError && meta.error && (
          <div className="phone-input-error">{meta.error}</div>
        )}
      </div>
    );
  }

  return (
    <FormField label={label} error={hasError ? meta.error : undefined}>
      <div
        className={tw(
          'phone-field flex items-center rounded-lg border bg-whiteLight px-3 py-2 text-sm text-ink transition dark:bg-darkLight dark:text-ink-dark',
          hasError
            ? 'border-red-500'
            : isFocused
            ? 'border-secondary-500'
            : 'border-line dark:border-white/10',
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        {phoneInput}
      </div>
    </FormField>
  );
};

export default InternationalPhones;
